/**
 * Tags especiales de un patio: el de proveedores y el de pipas sin tag.
 *
 * Son tarjetas COMPARTIDAS: no identifican a una unidad sino a una clase de
 * visitante, así que casi todo lo que la plataforma deduce de "este tag es de
 * este vehículo" deja de valer para ellas. Las decisiones que dependen de eso
 * —qué tarjetas se pueden elegir, qué checks aplican a sus eventos— viven acá.
 *
 * Módulo puro a propósito: las pantallas que lo usan importan `utils/api` →
 * firebase, y en un worktree sin `apps/v5/.env` eso revienta al colectar el
 * test. `import type` se borra al compilar, así que no arrastra nada.
 */
import type {
  AccessCard,
  YardAccessEvent,
} from "../../store/oasis/hikvisionAccessStore";
import {
  TRUCK_YARD_GUARD_ROLE_ID,
  TRUCK_YARD_INSPECTOR_ROLE_ID,
  TRUCK_YARD_SUPERVISOR_ROLE_ID,
} from "../../hooks/useTruckYardRole";
import { resolveCardYardIds } from "./accessCardsSection/cardYards";

export type SpecialTagKind = "provider" | "untagged";

export interface YardSpecialTags {
  /** `card_number` del tag de proveedores. */
  provider: string | null;
  /** `card_number` del tag de pipas sin tag. */
  untagged: string | null;
}

export const SPECIAL_TAG_KINDS: readonly SpecialTagKind[] = [
  "provider",
  "untagged",
];

const clean = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

/**
 * `special_tags` como lo guarda el patio → la forma con la que trabaja el
 * formulario. Ausente equivale a "ninguno de los dos", y los números se
 * comparan RECORTADOS en todos lados: el backend los recorta antes de validar
 * y hay `card_number` legacy con espacios (truckYardController.js, `tagOf`).
 */
export function normalizeSpecialTags(raw: unknown): YardSpecialTags {
  const source =
    raw && typeof raw === "object"
      ? (raw as Partial<Record<SpecialTagKind, unknown>>)
      : {};
  return { provider: clean(source.provider), untagged: clean(source.untagged) };
}

export function specialTagsEqual(a: unknown, b: unknown): boolean {
  const left = normalizeSpecialTags(a);
  const right = normalizeSpecialTags(b);
  return left.provider === right.provider && left.untagged === right.untagged;
}

/**
 * El mismo número en los dos selectores no se puede guardar: el backend
 * responde `400 SpecialTagsMustDiffer`, y con razón —un evento de ese tag no
 * sabría si abrir la inspección de proveedor o la de pipa—. Se valida antes de
 * mandar para no gastar el viaje. Dos "sin asignar" no son el mismo tag.
 */
export function specialTagsCollide(tags: unknown): boolean {
  const { provider, untagged } = normalizeSpecialTags(tags);
  return provider !== null && provider === untagged;
}

/**
 * Operadores de patio: los mismos ids que `YARD_OPERATOR_ROLE_IDS` de
 * `hikvisionAccessAuthz.js`. Sin gate por compañía, igual que allá.
 */
const YARD_OPERATOR_ROLE_IDS = new Set([
  TRUCK_YARD_GUARD_ROLE_ID,
  TRUCK_YARD_INSPECTOR_ROLE_ID,
  TRUCK_YARD_SUPERVISOR_ROLE_ID,
]);

/**
 * ¿Puede este rol cambiar los tags especiales del patio? `PUT /truck-yard/yard`
 * responde 403 si un vigilante, inspector o supervisor los cambia. A ellos el
 * formulario les muestra el valor guardado y NO manda el campo: sin
 * `special_tags` en el body el backend no toca lo guardado, así que pueden
 * seguir editando el resto del patio.
 */
export function canChangeYardSpecialTags(
  roleId: string | null | undefined,
): boolean {
  return !roleId || !YARD_OPERATOR_ROLE_IDS.has(roleId);
}

/**
 * Lo que va en el campo `special_tags` del FormData, o `null` si el campo NO
 * debe viajar.
 *
 * Sólo viaja cuando cambió respecto de lo guardado, y no es una
 * micro-optimización: el backend valida las tarjetas en CADA envío del campo
 * (viva, sin vehículo ni conductor). Un tag guardado que después se asignó a
 * una unidad haría fallar con 409 cualquier edición del patio —cambiar el
 * teléfono de la caseta— aunque nadie tocara los tags. Sin el campo, el
 * backend no toca ni revalida lo guardado.
 *
 * Un rol que no puede cambiarlos nunca lo manda: es la mitad que le permite
 * seguir guardando el resto del patio sin un 403.
 */
export function specialTagsToSubmit(input: {
  canChange: boolean;
  saved: unknown;
  current: unknown;
}): YardSpecialTags | null {
  if (!input.canChange) return null;
  if (specialTagsEqual(input.saved, input.current)) return null;
  return normalizeSpecialTags(input.current);
}

export interface SpecialTagOption {
  /** El número recortado: es lo que se guarda en `special_tags`. */
  value: string;
  /** Etiqueta de la tarjeta, si tiene; el número va aparte. */
  cardLabel: string;
  /**
   * El valor guardado ya no corresponde a una tarjeta elegible (se dio de baja,
   * se asignó a una unidad, cambió de patio). Se sigue listando para que el
   * selector muestre lo que hay en la base en vez de aparecer vacío, y la
   * pantalla lo marca.
   */
  unavailable: boolean;
}

type CardFields = Pick<
  AccessCard,
  | "card_number"
  | "type"
  | "label"
  | "vehicle_id"
  | "driver_id"
  | "yard_ids"
  | "yard_id"
>;

export interface SpecialTagOptionsInput {
  kind: SpecialTagKind;
  /** Tarjetas de la empresa tal como las devuelve `/accessCardList`. */
  cards: readonly CardFields[];
  /** Patio que se edita; `null` en el alta, donde sólo valen las sin patio. */
  yardId: string | null | undefined;
  /** Lo que el selector tiene puesto ahora (el guardado, al abrir). */
  current: string | null | undefined;
  /** Lo elegido en el OTRO selector: no se ofrece acá. */
  other: string | null | undefined;
  /** Ids de vehículos con `is_provider === true` (la unidad "PROVEEDORES"). */
  providerVehicleIds?: ReadonlySet<string>;
}

/**
 * ¿Sirve esta tarjeta como tag especial? Espejo de la validación de
 * `PUT /truck-yard/yard`:
 *
 *  · sólo `type === "tag"` (una tarjeta de persona no pasa por la pluma);
 *  · del patio que se edita o sin patio;
 *  · sin conductor, y sin vehículo — salvo el tag de proveedores, que puede
 *    seguir asignado a una unidad `is_provider` (el esquema anterior, donde
 *    los proveedores compartían el tag de la unidad "PROVEEDORES").
 *
 * "Viva" no se comprueba acá porque no hace falta: `/accessCardList` ya
 * descarta las dadas de baja (`status_id` DELETED), que además conservan
 * `status: "active"`, así que mirar `status` no las distinguiría.
 */
export function isEligibleSpecialTagCard(
  card: CardFields,
  input: Pick<SpecialTagOptionsInput, "kind" | "yardId" | "providerVehicleIds">,
): boolean {
  if (card.type !== "tag") return false;
  if (!clean(card.card_number)) return false;
  if (card.driver_id) return false;
  if (card.vehicle_id) {
    const legacyProviderUnit =
      input.kind === "provider" &&
      !!input.providerVehicleIds?.has(card.vehicle_id);
    if (!legacyProviderUnit) return false;
  }
  const yards = resolveCardYardIds(card);
  if (yards.length === 0) return true;
  return !!input.yardId && yards.includes(input.yardId);
}

/**
 * Las opciones de UNO de los dos selectores. El valor actual va siempre
 * primero-o-en-su-lugar aunque ya no sea elegible, y lo elegido en el otro
 * selector no aparece: es la mitad de "un mismo tag no puede ir en los dos"
 * que se resuelve sin mostrar un error.
 */
export function specialTagOptions(
  input: SpecialTagOptionsInput,
): SpecialTagOption[] {
  const current = clean(input.current);
  const other = clean(input.other);
  const options: SpecialTagOption[] = [];
  const seen = new Set<string>();

  for (const card of input.cards) {
    const value = clean(card.card_number);
    if (!value || seen.has(value) || value === other) continue;
    if (!isEligibleSpecialTagCard(card, input)) continue;
    seen.add(value);
    options.push({
      value,
      cardLabel: (card.label || "").trim(),
      unavailable: false,
    });
  }

  if (current && !seen.has(current)) {
    const known = input.cards.find((c) => clean(c.card_number) === current);
    options.unshift({
      value: current,
      cardLabel: (known?.label || "").trim(),
      unavailable: true,
    });
  }

  return options;
}

export type SpecialTagErrorKey =
  | "yards.specialTags.errors.invalid"
  | "yards.specialTags.errors.mustDiffer"
  | "yards.specialTags.errors.cardNotFound"
  | "yards.specialTags.errors.assignedToVehicle"
  | "yards.specialTags.errors.reserved";

/**
 * Códigos del backend → clave i18n (namespace truckYard).
 *
 * Se mapea por el TEXTO y no por el status porque el store del patio se queda
 * sólo con `getError(err)`: `errorHandler.js` manda `req.t(err.message)` en
 * `meta.message`, y un código sin traducción en el backend viaja tal cual. Si
 * algún día el backend lo traduce, esto devuelve `null` y la pantalla muestra
 * ese texto en vez de uno genérico.
 */
const SPECIAL_TAG_ERRORS: Record<string, SpecialTagErrorKey> = {
  // El campo llegó ilegible (no era el JSON de `{provider, untagged}`). Desde
  // este formulario no debería pasar; si pasa, es un defecto de acá y el
  // texto lo dice en vez de culpar a un tag.
  InvalidSpecialTags: "yards.specialTags.errors.invalid",
  SpecialTagsMustDiffer: "yards.specialTags.errors.mustDiffer",
  SpecialTagCardNotFound: "yards.specialTags.errors.cardNotFound",
  SpecialTagAssignedToVehicle: "yards.specialTags.errors.assignedToVehicle",
  CardReservedAsSpecialTag: "yards.specialTags.errors.reserved",
};

export function specialTagErrorKey(
  message: string | null | undefined,
): SpecialTagErrorKey | null {
  const code = (message || "").trim();
  // `hasOwnProperty`: el texto viene de una respuesta HTTP, y en un objeto
  // literal "constructor" o "toString" resolverían contra Object.prototype.
  return Object.prototype.hasOwnProperty.call(SPECIAL_TAG_ERRORS, code)
    ? SPECIAL_TAG_ERRORS[code]
    : null;
}

// ─── Eventos de acceso ───────────────────────────────────────────────────────

type EventFields = Pick<
  YardAccessEvent,
  "special_tag" | "vehicle_id" | "vehicle_plate" | "manual_registration"
>;

/** El `special_tag` sellado en la ingesta, o `null` si no es uno conocido. */
export function eventSpecialTag(
  event: Pick<YardAccessEvent, "special_tag">,
): SpecialTagKind | null {
  return event.special_tag === "provider" || event.special_tag === "untagged"
    ? event.special_tag
    : null;
}

export type SpecialTagCheckMode =
  /** Los checks de siempre. */
  | "normal"
  /** Tag de proveedores: ningún check aplica. */
  | "provider_exempt"
  /** Pipa sin tag que todavía no pasó por el registro manual de Inspecciones. */
  | "untagged_pending";

/**
 * Qué checks le tocan a un evento según su tag especial.
 *
 * El de proveedores queda exento de TODO: los checks preguntan por la unidad
 * (¿está registrada?, ¿es de este patio?, ¿su tipo de agua es compatible?) y
 * el tag de proveedores no es de ninguna, así que cada entrada saldría con las
 * mismas alertas rojas sin que nadie pudiera resolverlas.
 *
 * La pipa sin tag es distinta: sí tiene una unidad detrás, sólo que todavía no
 * se sabe cuál. Mientras falte el registro manual, "sin identificador" y "tag
 * sin vehículo" serían ciertas pero inútiles —mandan a buscar un vehículo que
 * se captura en otra pantalla—, y se cambian por un aviso que dice dónde. Con
 * placa o vehículo ya capturados vuelve a los checks normales.
 */
export function specialTagCheckMode(event: EventFields): SpecialTagCheckMode {
  const tag = eventSpecialTag(event);
  if (tag === "provider") return "provider_exempt";
  if (tag === "untagged") {
    const registered =
      !!event.manual_registration ||
      !!event.vehicle_id ||
      !!(event.vehicle_plate || "").trim();
    return registered ? "normal" : "untagged_pending";
  }
  return "normal";
}
